import type { ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import {
  IconeAuditoria,
  IconeColetas,
  IconeConfiguracoes,
  IconeArmazem,
  IconeAtualizar,
  IconeDispositivos,
  IconeEtiqueta,
  IconeFila,
  IconeInventarios,
  IconeMarca,
  IconeMovimentacoes,
  IconeRelatorios,
  IconeUsuarios,
  IconeVisaoGeral,
} from '../componentes/Icones.js';
import type { UsuarioSessao } from '../autenticacao/tipos.js';
import { iniciaisDoNome } from '../autenticacao/tipos.js';
import type { Acao } from '../autorizacao/permissoes.js';
import { perfilPode } from '../autorizacao/permissoes.js';
import './NavegacaoLateral.css';

type ItemNavegacao = {
  readonly para: string;
  readonly rotulo: string;
  readonly Icone: ComponentType<{ tamanho?: number }>;
  /**
   * Item que é uma FERRAMENTA, e não uma leitura: só aparece para o perfil
   * que pode usá-la. As nove seções do protótipo não têm `acao` — são
   * leitura para os três perfis e nunca somem.
   */
  readonly acao?: Acao;
};

type GrupoNavegacao = {
  readonly rotulo: string;
  readonly itens: readonly ItemNavegacao[];
};

/** A tela das divergências de saldo com o ERP (r1-sync). */
const CAMINHO_DIVERGENCIAS_DE_SALDO = '/saldos/divergencias';

/**
 * As nove seções do protótipo, na ordem dele, agrupadas pelo que a pessoa está
 * fazendo: operar o armazém, administrar quem e o que entra nele, e olhar
 * para trás. Os rótulos e a ordem não mudam de uma entrega para outra.
 *
 * A décima, Divergências de saldo, não está no protótipo: nasceu com a
 * sincronização do saldo com o ERP (r1-sync), e entra em Operação porque é
 * decisão do turno, ao lado das divergências da movimentação. O selo dela é a
 * notificação das que esperam decisão.
 *
 * A décima primeira, Sincronização, também nasceu do r1-sync: o estado da
 * leitura do ERP e o que ela gravou. Fica em Administração, ao lado de
 * Depósitos, que vêm da mesma integração.
 */
const GRUPOS: readonly GrupoNavegacao[] = [
  {
    rotulo: 'Operação',
    itens: [
      { para: '/visao-geral', rotulo: 'Visão geral', Icone: IconeVisaoGeral },
      { para: '/coletas', rotulo: 'Coletas', Icone: IconeColetas },
      { para: '/inventarios', rotulo: 'Inventários', Icone: IconeInventarios },
      { para: '/movimentacoes', rotulo: 'Movimentações', Icone: IconeMovimentacoes },
      {
        para: CAMINHO_DIVERGENCIAS_DE_SALDO,
        rotulo: 'Divergências de saldo',
        Icone: IconeAtualizar,
      },
    ],
  },
  {
    rotulo: 'Administração',
    itens: [
      { para: '/dispositivos', rotulo: 'Dispositivos', Icone: IconeDispositivos },
      { para: '/depositos', rotulo: 'Depósitos', Icone: IconeArmazem },
      { para: '/sincronizacao', rotulo: 'Sincronização', Icone: IconeFila },
      {
        para: '/etiquetas',
        rotulo: 'Etiquetas',
        Icone: IconeEtiqueta,
        acao: 'etiquetas.imprimir',
      },
      { para: '/usuarios', rotulo: 'Usuários', Icone: IconeUsuarios },
    ],
  },
  {
    rotulo: 'Acompanhamento',
    itens: [
      { para: '/relatorios', rotulo: 'Relatórios', Icone: IconeRelatorios },
      { para: '/auditoria', rotulo: 'Auditoria', Icone: IconeAuditoria },
      { para: '/configuracoes', rotulo: 'Configurações', Icone: IconeConfiguracoes },
    ],
  },
];

/** Os itens do grupo que o perfil da sessão enxerga. */
function itensVisiveis(
  itens: readonly ItemNavegacao[],
  usuario: UsuarioSessao,
): readonly ItemNavegacao[] {
  return itens.filter((item) => item.acao === undefined || perfilPode(usuario.perfil, item.acao));
}

type Props = {
  readonly usuario: UsuarioSessao;
  readonly nomeTenant: string;
  /** Coletas no período padrão; `null` enquanto a resposta não chegou. */
  readonly coletas: number | null;
  /** Aparelhos bloqueados agora, vindos da API. `null` = ainda não se sabe. */
  readonly dispositivosBloqueados: number | null;
  /** Divergências de saldo esperando decisão. `null` = ainda não se sabe. */
  readonly divergenciasDeSaldo: number | null;
  /** Trilho só de ícones — nas telas de detalhe e quando a pessoa recolhe. */
  readonly compacta: boolean;
};

/**
 * Trilho de navegação, na cor do fundo da moldura.
 *
 * O item ativo é um cartão branco levemente elevado com o texto na cor da
 * marca — o mesmo desenho dos cartões da tela, para o olho ler "você está
 * aqui" sem precisar de uma superfície escura à parte.
 */
export function NavegacaoLateral({
  usuario,
  nomeTenant,
  coletas,
  dispositivosBloqueados,
  divergenciasDeSaldo,
  compacta,
}: Props) {
  return (
    <nav
      className={compacta ? 'nav nav--compacta' : 'nav'}
      aria-label="Navegação principal"
      data-compacta={compacta ? 'sim' : 'nao'}
    >
      <div className="nav__marca">
        <span className="nav__marca-simbolo">
          <IconeMarca tamanho={18} />
        </span>
        {!compacta && (
          <span className="nav__marca-texto">
            <span className="nav__marca-titulo">Gestão de estoque</span>
            <span className="nav__marca-tenant mono">{nomeTenant}</span>
          </span>
        )}
      </div>

      <div className="nav__grupos">
        {GRUPOS.map((grupo) => (
          <div key={grupo.rotulo} className="nav__grupo">
            {!compacta && <span className="nav__grupo-rotulo">{grupo.rotulo}</span>}
            <ul className="nav__lista">
              {itensVisiveis(grupo.itens, usuario).map(({ para, rotulo, Icone }) => (
                <li key={para}>
                  <NavLink
                    to={para}
                    className={({ isActive }) =>
                      isActive ? 'nav__item nav__item--ativo' : 'nav__item'
                    }
                    title={compacta ? rotulo : undefined}
                  >
                    <span className="nav__icone">
                      <Icone tamanho={18} />
                    </span>
                    <span className={compacta ? 'apenas-leitor-de-tela' : 'nav__rotulo'}>
                      {rotulo}
                    </span>
                    {!compacta && (
                      <ContadorItem
                        para={para}
                        coletas={coletas}
                        dispositivosBloqueados={dispositivosBloqueados}
                        divergenciasDeSaldo={divergenciasDeSaldo}
                      />
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="nav__usuario">
        <span className="nav__avatar" aria-hidden="true">
          {iniciaisDoNome(usuario.nome)}
        </span>
        {!compacta && (
          <span className="nav__usuario-texto">
            <span className="nav__usuario-nome">{usuario.nome}</span>
            <span className="nav__usuario-perfil">{usuario.perfil}</span>
          </span>
        )}
      </div>
    </nav>
  );
}

/**
 * Contador à direita do item: número discreto em Coletas, selo colorido nos
 * alertas.
 *
 * **Todos vêm da API, e nenhum aparece antes da resposta.** Selo vermelho é
 * alerta, e alerta chutado custa a confiança de todos os outros.
 */
function ContadorItem({
  para,
  coletas,
  dispositivosBloqueados,
  divergenciasDeSaldo,
}: {
  readonly para: string;
  readonly coletas: number | null;
  readonly dispositivosBloqueados: number | null;
  readonly divergenciasDeSaldo: number | null;
}) {
  if (para === '/coletas' && coletas !== null && coletas > 0) {
    return <span className="nav__contador">{coletas}</span>;
  }
  if (para === '/dispositivos' && dispositivosBloqueados !== null && dispositivosBloqueados > 0) {
    return (
      <span className="nav__selo nav__selo--erro" aria-label="Aparelhos bloqueados">
        {dispositivosBloqueados}
      </span>
    );
  }
  if (
    para === CAMINHO_DIVERGENCIAS_DE_SALDO &&
    divergenciasDeSaldo !== null &&
    divergenciasDeSaldo > 0
  ) {
    // Atenção, e não erro: é decisão esperando gente, não algo quebrado.
    return (
      <span className="nav__selo nav__selo--atencao" aria-label="Divergências de saldo pendentes">
        {divergenciasDeSaldo}
      </span>
    );
  }
  return null;
}
